import { useState, useEffect } from 'react';
import { Mail, Lock, Eye, EyeOff, Loader2, ArrowLeft, User, CheckCircle2, RefreshCw, ArrowRight } from 'lucide-react';
import { useAuthStore } from '@/stores/auth-store';
import { sendPasswordResetEmail, sendVerificationEmail, reloadUser, fetchSignInMethodsForEmail } from '@/lib/firebase/auth';
import { readHandoffEmail } from '@/lib/account-handoff';
import { isEmbeddedIOSWebView } from '@/lib/auth/embedded-webview';

export function LoginForm() {
  // Pre-fill from a cross-property hand-off (`?suggestLoginMail=`) — the
  // account-mismatch banner links here with the account the user is meant to
  // switch to, so retyping it would just invite another mismatch.
  const [email, setEmail] = useState(() => readHandoffEmail() || '');
  const [password, setPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetStatus, setResetStatus] = useState<'idle' | 'sending' | 'sent' | 'error'>('idle');
  const [resetError, setResetError] = useState('');
  // Google sign-in cannot complete in an iOS app's web view (e.g. monday.com
  // iOS) — hide it there and let Google-only accounts set a password instead.
  const [googleUnavailable] = useState(isEmbeddedIOSWebView);
  const [setupStatus, setSetupStatus] = useState<'idle' | 'sending' | 'sent'>('idle');
  const signIn = useAuthStore((s) => s.signIn);
  const signUp = useAuthStore((s) => s.signUp);
  const signInWithGoogle = useAuthStore((s) => s.signInWithGoogle);
  const error = useAuthStore((s) => s.error);
  const needsSignUp = useAuthStore((s) => s.needsSignUp);
  const needsEmailVerification = useAuthStore((s) => s.needsEmailVerification);
  const pendingSignUpEmail = useAuthStore((s) => s.pendingSignUpEmail);
  const pendingSignUpPassword = useAuthStore((s) => s.pendingSignUpPassword);
  const user = useAuthStore((s) => s.user);
  // Use local loading state so the button isn't disabled by the global auth init
  const [submitting, setSubmitting] = useState(false);
  const [verificationSent, setVerificationSent] = useState(false);
  const [verificationChecking, setVerificationChecking] = useState(false);

  // ── Pre-login provider detection ──
  //
  // Step 'email'    → only the email input is shown. On "Continue" we probe
  //                   Firebase with fetchSignInMethodsForEmail to learn which
  //                   provider this account uses (requires email-enumeration
  //                   protection to be OFF in Firebase Console).
  // Step 'password' → account exists and has a 'password' provider — show the
  //                   password input. If 'google.com' is also linked we still
  //                   show the Google button as a secondary option.
  // Step 'google'   → account exists but only has 'google.com' — hide password
  //                   fields entirely and prompt the user to continue with
  //                   Google (avoids "wrong password" confusion).
  const [step, setStep] = useState<'email' | 'password' | 'google'>('email');
  const [detecting, setDetecting] = useState(false);
  const [methods, setMethods] = useState<string[]>([]);
  /** True when the sign-up form was reached by auto-switch from the login
   *  flow (no account existed for the typed email). In that case we show
   *  the email as a read-only pill with a "Change" affordance, matching
   *  the password/google steps, so the user doesn't retype. */
  const [autoSwitchedFromLogin, setAutoSwitchedFromLogin] = useState(false);

  const handleEmailContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) return;
    setDetecting(true);
    useAuthStore.setState({ error: null });
    try {
      const found = await fetchSignInMethodsForEmail(email.trim());
      setMethods(found);
      if (found.length === 0) {
        // No account — hop straight into sign-up with the email pre-filled.
        // Track that this was an auto-switch so the sign-up form shows a
        // "Change email" pill instead of an editable input.
        setAutoSwitchedFromLogin(true);
        useAuthStore.setState({
          needsSignUp: true,
          pendingSignUpEmail: email.trim(),
          pendingSignUpPassword: '',
          error: null,
        });
      } else if (found.includes('password')) {
        setStep('password');
      } else {
        // Google-only (or other federated-only) account — skip the password field.
        setStep('google');
      }
    } catch (err: unknown) {
      // Surface Firebase error text without the auth/* code noise.
      const msg = err instanceof Error ? err.message : 'Failed to check email';
      useAuthStore.setState({
        error: msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim(),
      });
    } finally {
      setDetecting(false);
    }
  };

  const handleBackToEmail = () => {
    setStep('email');
    setPassword('');
    setMethods([]);
    setSetupStatus('idle');
    useAuthStore.setState({ error: null });
  };

  // Google-only account where Google sign-in can't run: send the password
  // reset email, which adds a password to the existing account, then show the
  // password step so the user can sign in once they come back.
  const handleSendSetupLink = async () => {
    setSetupStatus('sending');
    useAuthStore.setState({ error: null });
    try {
      await sendPasswordResetEmail(email.trim());
      setSetupStatus('sent');
      setStep('password');
    } catch (err: unknown) {
      setSetupStatus('idle');
      const msg = err instanceof Error ? err.message : 'Failed to send the setup link';
      useAuthStore.setState({
        error: msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim(),
      });
    }
  };

  /** Exit the auto-switched sign-up flow and return to the email step so
   *  the user can change the address they typed. Keeps the email value in
   *  local state (not cleared) so it remains visible and editable. */
  const handleChangeEmailFromSignUp = () => {
    setAutoSwitchedFromLogin(false);
    setStep('email');
    setMethods([]);
    useAuthStore.setState({
      needsSignUp: false,
      pendingSignUpEmail: null,
      pendingSignUpPassword: null,
      error: null,
    });
  };

  // Initialize auth listener so we can detect if user is already signed in
  useEffect(() => {
    const unsubscribe = useAuthStore.getState().initializeAuth();
    return unsubscribe;
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setSubmitting(true);
    await signIn(email, password);
    setSubmitting(false);
  };

  const handleSignUp = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || !pendingSignUpEmail || !pendingSignUpPassword) return;
    setSubmitting(true);
    await signUp(pendingSignUpEmail, pendingSignUpPassword, displayName.trim());
    setSubmitting(false);
  };

  const handleGoogleSignIn = async () => {
    setSubmitting(true);
    await signInWithGoogle();
    setSubmitting(false);
  };

  const handleForgotPassword = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!resetEmail) return;
    setResetStatus('sending');
    setResetError('');
    try {
      await sendPasswordResetEmail(resetEmail);
      setResetStatus('sent');
    } catch (err: unknown) {
      setResetStatus('error');
      const msg = err instanceof Error ? err.message : 'Failed to send reset email';
      setResetError(msg.replace('Firebase: ', '').replace(/\(auth\/.*\)/, '').trim());
    }
  };

  // ── Email verification screen ──
  if (needsEmailVerification) {
    return (
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <div className="mx-auto flex h-12 w-12 items-center justify-center rounded-full bg-primary/10">
            <Mail className="h-6 w-6 text-primary" />
          </div>
          <h1 className="text-2xl font-bold text-foreground">Verify your email</h1>
          <p className="text-sm text-muted-foreground">
            We've sent a verification email to <strong>{user?.email}</strong>. Please check your inbox including spam & notifications folder.
          </p>
        </div>

        <div className="space-y-3">
          <button
            onClick={async () => {
              try {
                await sendVerificationEmail();
                setVerificationSent(true);
              } catch { /* already sent recently */ }
            }}
            disabled={verificationSent}
            className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {verificationSent ? (
              <>
                <CheckCircle2 className="h-4 w-4 text-emerald-500" />
                Verification email sent
              </>
            ) : (
              <>
                <Mail className="h-4 w-4" />
                Send verification email
              </>
            )}
          </button>

          <button
            onClick={async () => {
              setVerificationChecking(true);
              try {
                const freshUser = await reloadUser();
                if (freshUser.emailVerified) {
                  useAuthStore.setState({ needsEmailVerification: false });
                  window.location.reload();
                } else {
                  useAuthStore.setState({ error: 'Email not yet verified. Please check your inbox and try again.' });
                }
              } catch {
                useAuthStore.setState({ error: 'Failed to check verification status.' });
              }
              setVerificationChecking(false);
            }}
            disabled={verificationChecking}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {verificationChecking ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <RefreshCw className="h-4 w-4" />
            )}
            I have verified
          </button>
        </div>

        {error && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <p className="text-center text-xs text-muted-foreground">
          If you did not receive any mail,{' '}
          <a href="https://apps.ulgebra.com/contact" target="_blank" rel="noopener noreferrer" className="text-primary hover:text-primary/80 font-medium transition-colors">
            Contact Us
          </a>
        </p>
      </div>
    );
  }

  if (showForgotPassword) {
    return (
      <div data-testid="forgot-password-form" className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <h1 data-testid="forgot-password-heading" className="text-2xl font-bold text-foreground">Reset password</h1>
          <p data-testid="forgot-password-subtitle" className="text-sm text-muted-foreground">
            Enter your email and we'll send you a reset link
          </p>
        </div>

        {resetStatus === 'sent' ? (
          <div className="space-y-4">
            <div data-testid="reset-success-message" className="rounded-lg bg-green-50 dark:bg-green-950/20 p-4 text-sm text-green-700 dark:text-green-400">
              Password reset email sent to <strong>{resetEmail}</strong>. Check your inbox.
            </div>
            <button
              data-testid="back-to-signin-btn"
              onClick={() => { setShowForgotPassword(false); setResetStatus('idle'); setResetEmail(''); }}
              className="w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted transition-colors flex items-center justify-center gap-2"
            >
              <ArrowLeft className="h-4 w-4" />
              Back to sign in
            </button>
          </div>
        ) : (
          <>
            {resetError && (
              <div data-testid="reset-error" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
                {resetError}
              </div>
            )}
            <form data-testid="reset-submit-form" onSubmit={handleForgotPassword} className="space-y-4">
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground" htmlFor="reset-email">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <input
                    id="reset-email"
                    type="email"
                    value={resetEmail}
                    onChange={(e) => setResetEmail(e.target.value)}
                    placeholder="you@example.com"
                    className="w-full rounded-lg border border-input bg-background px-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                    autoFocus
                    required
                  />
                </div>
              </div>
              <button
                type="submit"
                data-testid="send-reset-link-btn"
                disabled={resetStatus === 'sending'}
                className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
              >
                {resetStatus === 'sending' && <Loader2 className="h-4 w-4 animate-spin" />}
                Send reset link
              </button>
            </form>
            <button
              data-testid="back-to-signin-btn"
              onClick={() => { setShowForgotPassword(false); setResetStatus('idle'); setResetError(''); }}
              className="w-full text-sm text-muted-foreground hover:text-foreground transition-colors flex items-center justify-center gap-1"
            >
              <ArrowLeft className="h-3.5 w-3.5" />
              Back to sign in
            </button>
          </>
        )}
      </div>
    );
  }

  // ── Sign-up: full form with email, password, and name ──
  if (needsSignUp) {
    return (
      <div className="w-full max-w-sm space-y-6">
        <div className="text-center space-y-2">
          <h1 className="text-2xl font-bold text-foreground">Create your account</h1>
          <p className="text-sm text-muted-foreground">
            Enter your details to sign up.
          </p>
        </div>

        {error && (
          <div className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
            {error}
          </div>
        )}

        <form onSubmit={handleSignUp} className="space-y-4">
          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="signup-name">
              Name
            </label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id="signup-name"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Enter your full name"
                className="w-full rounded-lg border border-input bg-background px-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                autoFocus
                required
              />
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="signup-email">
              Email
            </label>
            {autoSwitchedFromLogin ? (
              // Auto-switched from login (no account for typed email) — show
              // the email as a read-only pill with a Change affordance, so the
              // user can fix a typo without retyping.
              <button
                type="button"
                onClick={handleChangeEmailFromSignUp}
                className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors group"
              >
                <span className="flex items-center gap-2 min-w-0">
                  <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
                  <span className="truncate">{pendingSignUpEmail || ''}</span>
                </span>
                <span className="text-xs text-muted-foreground group-hover:text-foreground shrink-0">Change</span>
              </button>
            ) : (
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="signup-email"
                  type="email"
                  value={pendingSignUpEmail || ''}
                  onChange={(e) => useAuthStore.setState({ pendingSignUpEmail: e.target.value })}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-input bg-background px-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                  required
                />
              </div>
            )}
          </div>

          <div className="space-y-2">
            <label className="text-sm font-medium text-foreground" htmlFor="signup-password">
              Password
            </label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                id="signup-password"
                type={showPassword ? 'text' : 'password'}
                value={pendingSignUpPassword || ''}
                onChange={(e) => useAuthStore.setState({ pendingSignUpPassword: e.target.value })}
                placeholder="Create a password"
                className="w-full rounded-lg border border-input bg-background px-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          <button
            type="submit"
            disabled={submitting || !displayName.trim() || !pendingSignUpEmail || !pendingSignUpPassword}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
            Create Account
          </button>
        </form>

        <p className="text-center text-xs text-muted-foreground">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => {
              setAutoSwitchedFromLogin(false);
              useAuthStore.setState({ needsSignUp: false, pendingSignUpEmail: null, pendingSignUpPassword: null, error: null });
            }}
            className="text-primary hover:text-primary/80 font-medium transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    );
  }

  // Reusable "Continue with Google" button — rendered at different visual
  // weight depending on step (primary for google-only accounts, secondary
  // for password accounts that also have Google linked).
  const googleButton = (variant: 'primary' | 'secondary') => (
    <button
      data-testid="google-signin-btn"
      onClick={handleGoogleSignIn}
      disabled={submitting}
      className={
        variant === 'primary'
          ? 'w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2'
          : 'w-full rounded-lg border border-border px-4 py-2.5 text-sm font-medium text-foreground hover:bg-muted disabled:opacity-50 transition-colors flex items-center justify-center gap-2'
      }
    >
      {submitting ? (
        <Loader2 className="h-4 w-4 animate-spin" />
      ) : (
        <svg className="h-4 w-4" viewBox="0 0 24 24">
          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
        </svg>
      )}
      Continue with Google
    </button>
  );

  // Read-only email pill with a back button — shown on steps 2+ so the user
  // can see and change the email they entered without retyping it.
  const emailPill = (
    <button
      type="button"
      onClick={handleBackToEmail}
      className="w-full flex items-center justify-between gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2 text-sm text-foreground hover:bg-muted transition-colors group"
    >
      <span className="flex items-center gap-2 min-w-0">
        <Mail className="h-4 w-4 text-muted-foreground shrink-0" />
        <span className="truncate">{email}</span>
      </span>
      <span className="text-xs text-muted-foreground group-hover:text-foreground shrink-0">Change</span>
    </button>
  );

  return (
    <div data-testid="login-form" className="w-full max-w-sm space-y-6">
      <div className="text-center space-y-2">
        <h1 data-testid="login-heading" className="text-2xl font-bold text-foreground">Welcome back</h1>
        <p data-testid="login-subtitle" className="text-sm text-muted-foreground">
          Sign in to your Ulgebra account
        </p>
      </div>

      {error && (
        <div data-testid="login-error" className="rounded-lg bg-destructive/10 p-3 text-sm text-destructive">
          {error}
        </div>
      )}

      {/* ── Step 1: email + Continue ── */}
      {step === 'email' && (
        <>
          <form onSubmit={handleEmailContinue} className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium text-foreground" htmlFor="email">
                Email
              </label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="w-full rounded-lg border border-input bg-background px-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                  autoFocus
                  required
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={detecting || !email.trim()}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {detecting ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                <ArrowRight className="h-4 w-4" />
              )}
              Continue
            </button>
          </form>

          {!googleUnavailable && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-2 text-muted-foreground">
                    or
                  </span>
                </div>
              </div>

              {googleButton('secondary')}
            </>
          )}

          <p className="text-center text-xs text-muted-foreground">
            New user?{' '}
            <button
              type="button"
              onClick={() => {
                setAutoSwitchedFromLogin(false);
                useAuthStore.setState({ needsSignUp: true, pendingSignUpEmail: email, pendingSignUpPassword: password, error: null });
              }}
              className="text-primary hover:text-primary/80 font-medium transition-colors"
            >
              Sign up
            </button>
          </p>
        </>
      )}

      {/* ── Step 2a: account uses password (and possibly Google too) ── */}
      {step === 'password' && (
        <>
          {emailPill}

          {setupStatus === 'sent' && (
            <div className="rounded-lg bg-green-50 dark:bg-green-950/20 p-4 text-sm text-green-700 dark:text-green-400">
              We sent a link to <strong>{email}</strong>. Open it, set a password, then come back and sign in here.
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-foreground" htmlFor="password">
                  Password
                </label>
                <button
                  type="button"
                  data-testid="forgot-password-link"
                  onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
                  className="text-xs text-primary hover:text-primary/80 transition-colors"
                >
                  Forgot password?
                </button>
              </div>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <input
                  id="password"
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  className="w-full rounded-lg border border-input bg-background px-10 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20 transition-colors"
                  autoFocus
                  required
                />
                <button
                  type="button"
                  data-testid="password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              data-testid="login-submit-btn"
              disabled={submitting || !password}
              className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
            >
              {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
              Sign in
            </button>
          </form>

          {/* Google only shown here if the account has both providers linked. */}
          {methods.includes('google.com') && !googleUnavailable && (
            <>
              <div className="relative">
                <div className="absolute inset-0 flex items-center">
                  <div className="w-full border-t border-border" />
                </div>
                <div className="relative flex justify-center text-xs">
                  <span className="bg-background px-2 text-muted-foreground">
                    or
                  </span>
                </div>
              </div>
              {googleButton('secondary')}
            </>
          )}
        </>
      )}

      {/* ── Step 2b: Google-only account — hide password fields entirely ── */}
      {step === 'google' && googleUnavailable && (
        <>
          {emailPill}

          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            This account uses Google to sign in, which isn't available in this
            app. Set a password for it to sign in here — you can keep using
            Google everywhere else.
          </div>

          <button
            type="button"
            onClick={handleSendSetupLink}
            disabled={setupStatus === 'sending'}
            className="w-full rounded-lg bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors flex items-center justify-center gap-2"
          >
            {setupStatus === 'sending' ? <Loader2 className="h-4 w-4 animate-spin" /> : <Mail className="h-4 w-4" />}
            Email me a link to set a password
          </button>
        </>
      )}

      {step === 'google' && !googleUnavailable && (
        <>
          {emailPill}

          <div className="rounded-lg border border-border bg-muted/30 p-4 text-sm text-muted-foreground">
            This account uses Google to sign in. Continue with Google to access
            your Ulgebra workspace.
          </div>

          {googleButton('primary')}

          <div className="flex justify-center">
            <button
              type="button"
              data-testid="forgot-password-link"
              onClick={() => { setShowForgotPassword(true); setResetEmail(email); }}
              className="text-xs text-primary hover:text-primary/80 transition-colors"
            >
              Forgot password?
            </button>
          </div>
        </>
      )}
    </div>
  );
}
